import React, { useState, useMemo, useCallback } from 'react';
import { useLocale, formatCurrency } from '@platform/core';
import { Project, ProjectEntry, MeasureType, ProjectGoal } from '../../types/budget.types';
import { useBudget } from '../../contexts/BudgetContext';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// Helper to format currency safely (empty string causes Intl.NumberFormat to crash)
const formatAmount = (amount: number, currency?: string) => {
  try {
    return formatCurrency(amount, currency || undefined);
  } catch {
    return amount.toLocaleString();
  }
};

interface ProjectDetailsProps {
  project: Project;
  currency?: string;
  onEdit?: () => void;
}

type TabType = 'data' | 'charts' | 'settings';

// Available measures for project data entry
const PROJECT_MEASURES: MeasureType[] = ['budget', 'actual'];

export const ProjectDetails: React.FC<ProjectDetailsProps> = ({ project, currency, onEdit }) => {
  const { t } = useLocale();
  const { projectEntries, currentBudget, updateProjectEntries, updateProject } = useBudget();
  const [activeTab, setActiveTab] = useState<TabType>('data');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState<MeasureType | 'all'>('all');
  // editData: { "rowKey": { "period:measure": amount } } where rowKey is "subjectId" or "subjectId:sectionId"
  const [editData, setEditData] = useState<Record<string, Record<string, number>>>({});
  const [editPeriods, setEditPeriods] = useState<string[]>([]);
  // Settings tab - inline goal editing
  const [isEditingGoals, setIsEditingGoals] = useState(false);
  const [editGoals, setEditGoals] = useState<ProjectGoal[]>([]);
  const [isSavingGoals, setIsSavingGoals] = useState(false);

  const entries = projectEntries.filter(e => e.projectId === project.id);
  const sections = currentBudget?.sections || [];

  // Debug: Log entries for this project
  if (entries.length > 0) {
    console.log(`[ProjectDetails] Project "${project.name}" has ${entries.length} entries`);
    console.log('[ProjectDetails] Sample entry:', entries[0]);
    console.log('[ProjectDetails] Unique subjects in entries:', [...new Set(entries.map(e => e.subjectId))]);
    console.log('[ProjectDetails] Budget subjects:', currentBudget?.subjects?.map(s => ({ id: s.id, name: s.name })));
  }

  // Get sections for a subject
  const getSectionsForSubject = (subjectId: string) =>
    sections.filter(s => s.subjectId === subjectId);

  // Build row key for data entry
  const getRowKey = (subjectName: string, sectionName?: string) =>
    sectionName ? `${subjectName}:${sectionName}` : subjectName;

  // Parse row key back to subject/section
  const parseRowKey = (key: string) => {
    const parts = key.split(':');
    return { subjectName: parts[0], sectionName: parts[1] };
  };

  // Start editing mode
  const handleStartEdit = useCallback(() => {
    const subjects = currentBudget?.subjects || [];
    const budgetSections = currentBudget?.sections || [];
    const periods = [...new Set(entries.map(e => e.period))].sort((a, b) => {
      const [m1, y1] = a.split('/').map(Number);
      const [m2, y2] = b.split('/').map(Number);
      return (y1 * 12 + m1) - (y2 * 12 + m2);
    });

    // If no periods exist, create initial period based on project dates
    const initialPeriods = periods.length > 0 ? periods : (() => {
      const start = project.startDate ? new Date(project.startDate) : new Date();
      return [`${start.getMonth() + 1}/${start.getFullYear().toString().slice(-2)}`];
    })();

    // Initialize edit data from existing entries with measures
    const data: Record<string, Record<string, number>> = {};

    subjects.filter(s => s.type !== 'calculated' && s.type !== 'accumulative').forEach(subject => {
      const subjectSections = budgetSections.filter(sec => sec.subjectId === subject.id);

      if (subjectSections.length > 0) {
        // Subject has sections - create rows for each section
        subjectSections.forEach(section => {
          const rowKey = getRowKey(subject.name, section.name);
          data[rowKey] = {};
          initialPeriods.forEach(period => {
            PROJECT_MEASURES.forEach(measure => {
              const cellKey = `${period}:${measure}`;
              const entry = entries.find(e =>
                (e.subjectId === subject.name || e.subjectId === subject.id) &&
                (e.sectionId === section.name || e.sectionId === section.id) &&
                e.period === period &&
                e.measure === measure
              );
              data[rowKey][cellKey] = entry?.amount || 0;
            });
          });
        });
      } else {
        // Subject without sections
        const rowKey = getRowKey(subject.name);
        data[rowKey] = {};
        initialPeriods.forEach(period => {
          PROJECT_MEASURES.forEach(measure => {
            const cellKey = `${period}:${measure}`;
            const entry = entries.find(e =>
              (e.subjectId === subject.name || e.subjectId === subject.id) &&
              !e.sectionId &&
              e.period === period &&
              e.measure === measure
            );
            data[rowKey][cellKey] = entry?.amount || 0;
          });
        });
      }
    });

    setEditData(data);
    setEditPeriods(initialPeriods);
    setIsEditing(true);
  }, [entries, currentBudget?.subjects, currentBudget?.sections, project.startDate]);

  // Cancel editing
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditData({});
    setEditPeriods([]);
  }, []);

  // Save changes
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      const newEntries: Array<{ period: string; subjectId: string; sectionId?: string; measure: string; amount: number }> = [];
      Object.entries(editData).forEach(([rowKey, cells]) => {
        const { subjectName, sectionName } = parseRowKey(rowKey);
        Object.entries(cells).forEach(([cellKey, amount]) => {
          // cellKey format: "period:measure"
          const [period, measure] = cellKey.split(':');
          if (amount > 0) {
            newEntries.push({
              period,
              subjectId: subjectName,
              sectionId: sectionName,
              measure,
              amount,
            });
          }
        });
      });
      await updateProjectEntries(project.id, newEntries);
      setIsEditing(false);
      setEditData({});
      setEditPeriods([]);
    } catch (error) {
      console.error('Error saving project entries:', error);
    } finally {
      setIsSaving(false);
    }
  }, [editData, project.id, updateProjectEntries]);

  // Update a cell value (cellKey format: "period:measure")
  const handleCellChange = useCallback((rowKey: string, cellKey: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setEditData(prev => ({
      ...prev,
      [rowKey]: {
        ...prev[rowKey],
        [cellKey]: numValue,
      },
    }));
  }, []);

  // Add a new period
  const handleAddPeriod = useCallback(() => {
    // Get next period based on last one
    const lastPeriod = editPeriods[editPeriods.length - 1];
    let newPeriod: string;
    if (lastPeriod) {
      const [month, year] = lastPeriod.split('/').map(Number);
      const nextMonth = month === 12 ? 1 : month + 1;
      const nextYear = month === 12 ? year + 1 : year;
      newPeriod = `${nextMonth}/${nextYear.toString().slice(-2)}`;
    } else {
      const now = new Date();
      newPeriod = `${now.getMonth() + 1}/${now.getFullYear().toString().slice(-2)}`;
    }

    setEditPeriods(prev => [...prev, newPeriod]);
    // Initialize all subjects for the new period with all measures
    setEditData(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(rowKey => {
        PROJECT_MEASURES.forEach(measure => {
          const cellKey = `${newPeriod}:${measure}`;
          updated[rowKey] = { ...updated[rowKey], [cellKey]: 0 };
        });
      });
      return updated;
    });
  }, [editPeriods]);

  // Remove a period
  const handleRemovePeriod = useCallback((period: string) => {
    setEditPeriods(prev => prev.filter(p => p !== period));
    setEditData(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(rowKey => {
        // Remove all measure entries for this period
        const newCells: Record<string, number> = {};
        Object.entries(updated[rowKey]).forEach(([cellKey, value]) => {
          if (!cellKey.startsWith(`${period}:`)) {
            newCells[cellKey] = value;
          }
        });
        updated[rowKey] = newCells;
      });
      return updated;
    });
  }, []);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (entries.length === 0) return null;

    const periods = [...new Set(entries.map(e => e.period))].sort((a, b) => {
      // Sort by period (format: M/YY or MM/YY)
      const [m1, y1] = a.split('/').map(Number);
      const [m2, y2] = b.split('/').map(Number);
      return (y1 * 12 + m1) - (y2 * 12 + m2);
    });

    const subjects = currentBudget?.subjects || [];
    const creditSubjects = subjects.filter(s => s.type === 'credit');
    const debitSubjects = subjects.filter(s => s.type === 'debit');

    // Calculate income and expenses by period
    const incomeByPeriod = periods.map(period => {
      return entries
        .filter(e => e.period === period && creditSubjects.some(s => s.name === e.subjectId || s.id === e.subjectId))
        .reduce((sum, e) => sum + e.amount, 0);
    });

    const expensesByPeriod = periods.map(period => {
      return entries
        .filter(e => e.period === period && debitSubjects.some(s => s.name === e.subjectId || s.id === e.subjectId))
        .reduce((sum, e) => sum + e.amount, 0);
    });

    const profitByPeriod = periods.map((_, i) => incomeByPeriod[i] - expensesByPeriod[i]);

    // Calculate totals by subject for pie chart
    const subjectTotals = subjects.filter(s => s.type !== 'calculated').map(subject => ({
      name: subject.name,
      type: subject.type,
      total: entries
        .filter(e => e.subjectId === subject.name || e.subjectId === subject.id)
        .reduce((sum, e) => sum + e.amount, 0),
    }));

    return { periods, incomeByPeriod, expensesByPeriod, profitByPeriod, subjectTotals };
  }, [entries, currentBudget?.subjects]);

  const renderDataTab = () => {
    const subjects = currentBudget?.subjects || [];
    const budgetSections = currentBudget?.sections || [];

    // Build edit rows - includes both subject-only rows and subject:section rows
    const getEditRows = () => {
      const rows: Array<{ key: string; name: string; type: string; isSection: boolean; subjectName?: string }> = [];

      subjects.filter(s => s.type !== 'calculated' && s.type !== 'accumulative').forEach(subject => {
        const subjectSections = budgetSections.filter(sec => sec.subjectId === subject.id);

        if (subjectSections.length > 0) {
          // Add subject header row (non-editable)
          rows.push({
            key: `header:${subject.name}`,
            name: subject.name,
            type: subject.type,
            isSection: false,
          });
          // Add section rows
          subjectSections.forEach(section => {
            rows.push({
              key: getRowKey(subject.name, section.name),
              name: section.name,
              type: subject.type,
              isSection: true,
              subjectName: subject.name,
            });
          });
        } else {
          // Subject without sections - editable row
          rows.push({
            key: getRowKey(subject.name),
            name: subject.name,
            type: subject.type,
            isSection: false,
          });
        }
      });

      return rows;
    };

    // Get visible measures based on filter
    const visibleMeasures = selectedMeasure === 'all' ? PROJECT_MEASURES : [selectedMeasure];

    // Editing mode
    if (isEditing) {
      const editRows = getEditRows();

      return (
        <div className="data-tab">
          <div className="data-toolbar">
            <div className="toolbar-left">
              <button className="btn btn-sm btn-primary" onClick={handleAddPeriod}>
                + {t('budget.projects.addPeriod')}
              </button>
              <div className="measure-toggle">
                <span>{t('budget.measure')}:</span>
                <select
                  value={selectedMeasure}
                  onChange={(e) => setSelectedMeasure(e.target.value as MeasureType | 'all')}
                >
                  <option value="all">{t('common.all')}</option>
                  <option value="budget">{t('budget.budget')}</option>
                  <option value="actual">{t('budget.actual')}</option>
                </select>
              </div>
            </div>
            <div className="toolbar-actions">
              <button className="btn btn-sm btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>
                {t('common.cancel')}
              </button>
              <button className="btn btn-sm btn-primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
          <div className="overview-table-wrapper">
            <table className="overview-table editable">
              <thead>
                <tr>
                  <th rowSpan={2}>{t('budget.subject')}</th>
                  {editPeriods.map(period => (
                    <th key={period} colSpan={visibleMeasures.length} className="text-center period-header-group">
                      <span>{period}</span>
                      <button
                        className="remove-period-btn"
                        onClick={() => handleRemovePeriod(period)}
                        title={t('common.delete')}
                      >
                        ×
                      </button>
                    </th>
                  ))}
                  <th rowSpan={2} className="text-end">{t('common.total')}</th>
                </tr>
                <tr>
                  {editPeriods.map(period => (
                    visibleMeasures.map(measure => (
                      <th key={`${period}:${measure}`} className="text-end measure-header">
                        {t(`budget.${measure}`)}
                      </th>
                    ))
                  ))}
                </tr>
              </thead>
              <tbody>
                {editRows.map(row => {
                  const isHeaderRow = row.key.startsWith('header:');
                  // Calculate total for actual measure only
                  const total = isHeaderRow ? 0 : editPeriods.reduce((sum, period) => {
                    const cellKey = `${period}:actual`;
                    return sum + (editData[row.key]?.[cellKey] || 0);
                  }, 0);

                  return (
                    <tr
                      key={row.key}
                      className={`${row.type === 'credit' ? 'income-row' : 'expense-row'} ${row.isSection ? 'section-row' : ''} ${isHeaderRow ? 'header-row' : ''}`}
                    >
                      <td className={row.isSection ? 'section-cell' : ''}>
                        {row.isSection && <span className="section-indent">└</span>}
                        {row.name}
                      </td>
                      {editPeriods.map(period => (
                        visibleMeasures.map(measure => {
                          const cellKey = `${period}:${measure}`;
                          return (
                            <td key={cellKey} className={`text-end editable-cell measure-${measure}`}>
                              {isHeaderRow ? (
                                <span className="header-spacer">-</span>
                              ) : (
                                <input
                                  type="number"
                                  value={editData[row.key]?.[cellKey] || ''}
                                  onChange={(e) => handleCellChange(row.key, cellKey, e.target.value)}
                                  placeholder="0"
                                />
                              )}
                            </td>
                          );
                        })
                      ))}
                      <td className="text-end total-cell">
                        {isHeaderRow ? '-' : formatAmount(total, currency)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    // View mode
    if (entries.length === 0) {
      return (
        <div className="empty-data">
          <p>{t('budget.projects.noData')}</p>
          <button className="btn btn-sm btn-primary" onClick={handleStartEdit}>
            + {t('budget.projects.addData')}
          </button>
        </div>
      );
    }

    // Group entries by period for overview table
    const periods = [...new Set(entries.map(e => e.period))].sort((a, b) => {
      const [m1, y1] = a.split('/').map(Number);
      const [m2, y2] = b.split('/').map(Number);
      return (y1 * 12 + m1) - (y2 * 12 + m2);
    });

    // Build view rows with sections
    const getViewRows = () => {
      const rows: Array<{ key: string; name: string; type: string; isSection: boolean; subjectId: string; sectionId?: string }> = [];

      subjects.filter(s => s.type !== 'calculated' && s.type !== 'accumulative').forEach(subject => {
        const subjectSections = budgetSections.filter(sec => sec.subjectId === subject.id);

        // Subject row (aggregate of all sections or direct entries)
        rows.push({
          key: `subject:${subject.id}`,
          name: subject.name,
          type: subject.type,
          isSection: false,
          subjectId: subject.id,
        });

        // Section rows
        subjectSections.forEach(section => {
          rows.push({
            key: `section:${section.id}`,
            name: section.name,
            type: subject.type,
            isSection: true,
            subjectId: subject.id,
            sectionId: section.id,
          });
        });
      });

      return rows;
    };

    const viewRows = getViewRows();

    return (
      <div className="data-tab">
        <div className="data-toolbar">
          <div className="toolbar-left">
            <button className="btn btn-sm btn-secondary" onClick={handleStartEdit}>
              {t('common.edit')}
            </button>
            <div className="measure-toggle">
              <span>{t('budget.measure')}:</span>
              <select
                value={selectedMeasure}
                onChange={(e) => setSelectedMeasure(e.target.value as MeasureType | 'all')}
              >
                <option value="all">{t('common.all')}</option>
                <option value="budget">{t('budget.budget')}</option>
                <option value="actual">{t('budget.actual')}</option>
              </select>
            </div>
          </div>
        </div>
        <div className="overview-table-wrapper">
          <table className="overview-table">
            <thead>
              <tr>
                <th rowSpan={visibleMeasures.length > 1 ? 2 : 1}>{t('budget.subject')}</th>
                {periods.map(period => (
                  <th key={period} colSpan={visibleMeasures.length} className="text-center">
                    {period}
                  </th>
                ))}
                <th rowSpan={visibleMeasures.length > 1 ? 2 : 1} className="text-end">{t('common.total')}</th>
              </tr>
              {visibleMeasures.length > 1 && (
                <tr>
                  {periods.map(period => (
                    visibleMeasures.map(measure => (
                      <th key={`${period}:${measure}`} className="text-end measure-header">
                        {t(`budget.${measure}`)}
                      </th>
                    ))
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {viewRows.map(row => {
                // Get entries for this row
                const rowEntries = row.isSection
                  ? entries.filter(e =>
                      (e.subjectId === row.subjectId || e.subjectId === subjects.find(s => s.id === row.subjectId)?.name) &&
                      (e.sectionId === row.sectionId || e.sectionId === budgetSections.find(s => s.id === row.sectionId)?.name)
                    )
                  : entries.filter(e =>
                      (e.subjectId === row.subjectId || e.subjectId === subjects.find(s => s.id === row.subjectId)?.name) &&
                      !e.sectionId
                    );

                // Calculate total for actual measure
                const total = rowEntries.filter(e => e.measure === 'actual').reduce((sum, e) => sum + e.amount, 0);

                return (
                  <tr
                    key={row.key}
                    className={`${row.type === 'credit' ? 'income-row' : 'expense-row'} ${row.isSection ? 'section-row' : ''}`}
                  >
                    <td className={row.isSection ? 'section-cell' : ''}>
                      {row.isSection && <span className="section-indent">└</span>}
                      {row.name}
                    </td>
                    {periods.map(period => (
                      visibleMeasures.map(measure => {
                        const entry = rowEntries.find(e => e.period === period && e.measure === measure);
                        return (
                          <td key={`${period}:${measure}`} className={`text-end measure-${measure}`}>
                            {entry ? formatAmount(entry.amount, currency) : '-'}
                          </td>
                        );
                      })
                    ))}
                    <td className="text-end total-cell">
                      {total > 0 ? formatAmount(total, currency) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderChartsTab = () => {
    if (!chartData) {
      return (
        <div className="charts-tab">
          <p className="placeholder-text">{t('budget.projects.noData')}</p>
        </div>
      );
    }

    // Prepare data for recharts format
    const trendData = chartData.periods.map((period, idx) => ({
      period,
      [t('budget.income')]: chartData.incomeByPeriod[idx],
      [t('budget.expenses')]: chartData.expensesByPeriod[idx],
      [t('budget.profit')]: chartData.profitByPeriod[idx],
    }));

    const incomeSubjects = chartData.subjectTotals.filter(s => s.type === 'credit' && s.total > 0);
    const expenseSubjects = chartData.subjectTotals.filter(s => s.type === 'debit' && s.total > 0);

    const INCOME_COLORS = ['#22c55e', '#10b981', '#059669', '#047857'];
    const EXPENSE_COLORS = ['#ef4444', '#dc2626', '#b91c1c', '#991b1b'];

    return (
      <div className="charts-tab">
        <div className="charts-grid">
          <div className="chart-card">
            <h5>{t('budget.projects.incomeExpensesTrend')}</h5>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey={t('budget.income')} stroke="#22c55e" strokeWidth={2} />
                  <Line type="monotone" dataKey={t('budget.expenses')} stroke="#ef4444" strokeWidth={2} />
                  <Line type="monotone" dataKey={t('budget.profit')} stroke="#3b82f6" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="chart-card">
            <h5>{t('budget.projects.incomeExpensesComparison')}</h5>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey={t('budget.income')} fill="#22c55e" />
                  <Bar dataKey={t('budget.expenses')} fill="#ef4444" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          {incomeSubjects.length > 0 && (
            <div className="chart-card">
              <h5>{t('budget.projects.incomeDistribution')}</h5>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={incomeSubjects}
                      dataKey="total"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {incomeSubjects.map((_, idx) => (
                        <Cell key={idx} fill={INCOME_COLORS[idx % INCOME_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {expenseSubjects.length > 0 && (
            <div className="chart-card">
              <h5>{t('budget.projects.expenseDistribution')}</h5>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={expenseSubjects}
                      dataKey="total"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {expenseSubjects.map((_, idx) => (
                        <Cell key={idx} fill={EXPENSE_COLORS[idx % EXPENSE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Goal management functions for Settings tab
  const handleStartEditGoals = useCallback(() => {
    const subjects = currentBudget?.subjects || [];
    const budgetSections = currentBudget?.sections || [];

    // Initialize goals from project or build from budget structure
    let initialGoals: ProjectGoal[] = [...(project.goals || [])];

    // Ensure all budget subjects/sections have a goal entry
    subjects.filter(s => s.type !== 'calculated' && s.type !== 'accumulative').forEach(subject => {
      const subjectSections = budgetSections.filter(sec => sec.subjectId === subject.id);

      if (subjectSections.length > 0) {
        subjectSections.forEach(section => {
          const exists = initialGoals.some(g =>
            g.subjectName === subject.name && g.sectionName === section.name
          );
          if (!exists) {
            initialGoals.push({
              subjectName: subject.name,
              sectionName: section.name,
              goalPercent: section.incomePartGoal || 0,
            });
          }
        });
      } else {
        const exists = initialGoals.some(g =>
          g.subjectName === subject.name && !g.sectionName
        );
        if (!exists) {
          initialGoals.push({
            subjectName: subject.name,
            goalPercent: subject.goalPercentage || 0,
          });
        }
      }
    });

    setEditGoals(initialGoals);
    setIsEditingGoals(true);
  }, [project.goals, currentBudget?.subjects, currentBudget?.sections]);

  const handleCancelEditGoals = useCallback(() => {
    setIsEditingGoals(false);
    setEditGoals([]);
  }, []);

  const handleSaveGoals = useCallback(async () => {
    setIsSavingGoals(true);
    try {
      await updateProject(project.id, { goals: editGoals });
      setIsEditingGoals(false);
      setEditGoals([]);
    } catch (error) {
      console.error('Error saving goals:', error);
    } finally {
      setIsSavingGoals(false);
    }
  }, [editGoals, project.id, updateProject]);

  const handleGoalChange = useCallback((subjectName: string, sectionName: string | undefined, value: number) => {
    setEditGoals(prev => {
      const idx = prev.findIndex(g =>
        g.subjectName === subjectName &&
        (sectionName ? g.sectionName === sectionName : !g.sectionName)
      );
      if (idx >= 0) {
        return prev.map((g, i) => i === idx ? { ...g, goalPercent: value } : g);
      }
      return [...prev, { subjectName, sectionName, goalPercent: value }];
    });
  }, []);

  const handleResetGoalsToDefaults = useCallback(() => {
    const subjects = currentBudget?.subjects || [];
    const budgetSections = currentBudget?.sections || [];

    const defaultGoals: ProjectGoal[] = [];
    subjects.filter(s => s.type !== 'calculated' && s.type !== 'accumulative').forEach(subject => {
      const subjectSections = budgetSections.filter(sec => sec.subjectId === subject.id);

      if (subjectSections.length > 0) {
        subjectSections.forEach(section => {
          defaultGoals.push({
            subjectName: subject.name,
            sectionName: section.name,
            goalPercent: section.incomePartGoal || 0,
          });
        });
      } else {
        defaultGoals.push({
          subjectName: subject.name,
          goalPercent: subject.goalPercentage || 0,
        });
      }
    });

    setEditGoals(defaultGoals);
  }, [currentBudget?.subjects, currentBudget?.sections]);

  const getEditGoalValue = useCallback((subjectName: string, sectionName?: string): number => {
    const goal = editGoals.find(g =>
      g.subjectName === subjectName &&
      (sectionName ? g.sectionName === sectionName : !g.sectionName)
    );
    return goal?.goalPercent ?? 0;
  }, [editGoals]);

  const renderSettingsTab = () => {
    const subjects = currentBudget?.subjects || [];
    const sections = currentBudget?.sections || [];

    // Group subjects by type
    const creditSubjects = subjects.filter(s => s.type === 'credit');
    const debitSubjects = subjects.filter(s => s.type === 'debit');
    const calculatedSubjects = subjects.filter(s => s.type === 'calculated' || s.type === 'accumulative');

    // Get sections for a subject
    const getSectionsForSubject = (subjectId: string) =>
      sections.filter(s => s.subjectId === subjectId);

    // Get goal for a subject
    const getGoal = (subjectName: string, sectionName?: string) => {
      const goal = project.goals?.find(g =>
        g.subjectName === subjectName &&
        (sectionName ? g.sectionName === sectionName : !g.sectionName)
      );
      return goal?.goalPercent;
    };

    const renderSubjectGroup = (groupSubjects: typeof subjects, title: string, colorClass: string) => {
      if (groupSubjects.length === 0) return null;

      return (
        <div className="subject-group">
          <h5 className={`subject-group-title ${colorClass}`}>{title}</h5>
          <div className="subjects-list">
            {groupSubjects.map(subject => {
              const subjectSections = getSectionsForSubject(subject.id);
              const goal = isEditingGoals ? getEditGoalValue(subject.name) : getGoal(subject.name);

              return (
                <div key={subject.id} className="subject-card">
                  <div className="subject-header">
                    <span className="subject-name">{subject.name}</span>
                    {subjectSections.length === 0 && (
                      isEditingGoals ? (
                        <div className="goal-input-inline">
                          <input
                            type="number"
                            value={goal || 0}
                            onChange={e => handleGoalChange(subject.name, undefined, parseFloat(e.target.value) || 0)}
                            min="0"
                            max="100"
                            step="0.1"
                          />
                          <span>%</span>
                        </div>
                      ) : (
                        goal !== undefined && <span className="subject-goal">{goal}%</span>
                      )
                    )}
                  </div>
                  {subjectSections.length > 0 && (
                    <div className="sections-list">
                      {subjectSections.map(section => {
                        const sectionGoal = isEditingGoals
                          ? getEditGoalValue(subject.name, section.name)
                          : (getGoal(subject.name, section.name) ?? section.incomePartGoal);
                        return (
                          <div key={section.id} className="section-item">
                            <span className="section-name">{section.name}</span>
                            {isEditingGoals ? (
                              <div className="goal-input-inline">
                                <input
                                  type="number"
                                  value={sectionGoal || 0}
                                  onChange={e => handleGoalChange(subject.name, section.name, parseFloat(e.target.value) || 0)}
                                  min="0"
                                  max="100"
                                  step="0.1"
                                />
                                <span>%</span>
                              </div>
                            ) : (
                              sectionGoal !== undefined && (
                                <span className="section-goal">{sectionGoal}%</span>
                              )
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div className="settings-tab">
        <div className="settings-header">
          <h4>{t('budget.projects.generalInfo')}</h4>
          {onEdit && (
            <button className="btn btn-sm btn-secondary" onClick={onEdit}>
              {t('common.edit')}
            </button>
          )}
        </div>
        <div className="settings-grid">
          <div className="settings-section">
            <div className="info-grid">
              <div className="info-item">
                <label>{t('budget.projects.name')}:</label>
                <span>{project.name}</span>
              </div>
              <div className="info-item">
                <label>{t('budget.projects.client')}:</label>
                <span>{project.client || '-'}</span>
              </div>
              <div className="info-item">
                <label>{t('budget.projects.projectManager')}:</label>
                <span>{project.projectManager || '-'}</span>
              </div>
              <div className="info-item">
                <label>{t('budget.projects.manager')}:</label>
                <span>{project.manager || '-'}</span>
              </div>
              <div className="info-item">
                <label>{t('budget.projects.contract')}:</label>
                <span>{project.contractNumber || '-'}</span>
              </div>
              <div className="info-item">
                <label>{t('budget.projects.startDate')}:</label>
                <span>{project.startDate || '-'}</span>
              </div>
              <div className="info-item">
                <label>{t('budget.projects.endDate')}:</label>
                <span>{project.endDate || '-'}</span>
              </div>
              <div className="info-item">
                <label>{t('budget.projects.budget')}:</label>
                <span>{project.budget !== undefined && project.budget !== null ? formatAmount(project.budget, currency) : '-'}</span>
              </div>
            </div>
          </div>

          {subjects.length > 0 && (
            <div className="settings-section">
              <div className="section-header">
                <h4>{t('budget.subjectsAndSections')}</h4>
                <div className="section-actions">
                  {isEditingGoals ? (
                    <>
                      <button
                        className="btn btn-sm btn-ghost"
                        onClick={handleResetGoalsToDefaults}
                        title={t('budget.projects.resetToDefaults')}
                      >
                        {t('common.reset')}
                      </button>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={handleCancelEditGoals}
                        disabled={isSavingGoals}
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={handleSaveGoals}
                        disabled={isSavingGoals}
                      >
                        {isSavingGoals ? t('common.saving') : t('common.save')}
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={handleStartEditGoals}
                    >
                      {t('budget.projects.editGoals')}
                    </button>
                  )}
                </div>
              </div>
              <div className="subjects-sections-grid">
                {renderSubjectGroup(creditSubjects, t('budget.income'), 'credit')}
                {renderSubjectGroup(debitSubjects, t('budget.expenses'), 'debit')}
                {renderSubjectGroup(calculatedSubjects, t('budget.calculated'), 'calculated')}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="project-details">
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'data' ? 'active' : ''}`}
          onClick={() => setActiveTab('data')}
        >
          {t('budget.projects.dataTab')}
        </button>
        <button
          className={`tab ${activeTab === 'charts' ? 'active' : ''}`}
          onClick={() => setActiveTab('charts')}
        >
          {t('budget.projects.chartsTab')}
        </button>
        <button
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          {t('budget.projects.settingsTab')}
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'data' && renderDataTab()}
        {activeTab === 'charts' && renderChartsTab()}
        {activeTab === 'settings' && renderSettingsTab()}
      </div>

      <style>{`
        .project-details {
          background: var(--color-background);
          border-radius: var(--radius);
        }

        .tabs {
          display: flex;
          gap: 0;
          border-bottom: 1px solid var(--color-border);
        }

        .tab {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          cursor: pointer;
          border-bottom: 2px solid transparent;
          margin-bottom: -1px;
          transition: all 0.2s;
        }

        .tab:hover {
          color: var(--color-text);
        }

        .tab.active {
          color: var(--color-primary);
          border-bottom-color: var(--color-primary);
        }

        .tab-content {
          padding: 1rem;
        }

        .empty-data {
          text-align: center;
          padding: 2rem;
          color: var(--color-text-secondary);
        }

        .overview-table-wrapper {
          overflow-x: auto;
        }

        .overview-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .overview-table th,
        .overview-table td {
          padding: 0.5rem;
          border: 1px solid var(--color-border);
        }

        .overview-table th {
          background: var(--color-surface);
          font-weight: 600;
        }

        .income-row {
          background: rgba(34, 197, 94, 0.05);
        }

        .expense-row {
          background: rgba(239, 68, 68, 0.05);
        }

        .total-cell {
          font-weight: 600;
          background: var(--color-surface);
        }

        .text-end {
          text-align: end;
        }

        .placeholder-text {
          text-align: center;
          color: var(--color-text-secondary);
          padding: 2rem;
        }

        .settings-grid {
          display: grid;
          gap: 1.5rem;
        }

        .settings-section h4 {
          margin: 0 0 0.75rem;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--color-text-secondary);
          text-transform: uppercase;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 0.75rem;
        }

        .info-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .info-item label {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
        }

        .info-item span {
          font-size: 0.875rem;
        }

        .goals-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .goals-table th,
        .goals-table td {
          padding: 0.5rem;
          border: 1px solid var(--color-border);
          text-align: start;
        }

        .goals-table th {
          background: var(--color-surface);
          font-weight: 600;
        }

        .settings-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }

        .settings-header h4 {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--color-text-secondary);
          text-transform: uppercase;
        }

        .btn-sm {
          padding: 0.25rem 0.75rem;
          font-size: 0.75rem;
          border-radius: var(--radius);
          cursor: pointer;
        }

        .btn-secondary {
          background: var(--color-background);
          color: var(--color-text);
          border: 1px solid var(--color-border);
        }

        .btn-secondary:hover {
          background: var(--color-border);
        }

        .charts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 1.5rem;
        }

        .chart-card {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          padding: 1rem;
        }

        .chart-card h5 {
          margin: 0 0 1rem;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .chart-container {
          height: 250px;
        }

        .chart-container.pie {
          height: 300px;
        }

        .data-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
          gap: 1rem;
        }

        .toolbar-actions {
          display: flex;
          gap: 0.5rem;
        }

        .toolbar-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .measure-toggle {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .measure-toggle select {
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          font-size: 0.875rem;
        }

        .period-header-group {
          position: relative;
          border-bottom: 1px solid var(--color-border);
        }

        .measure-header {
          font-size: 0.7rem;
          color: var(--color-text-secondary);
          font-weight: 500;
        }

        .measure-budget {
          background: rgba(59, 130, 246, 0.05);
        }

        .measure-actual {
          background: rgba(34, 197, 94, 0.05);
        }

        .btn {
          padding: 0.5rem 1rem;
          border-radius: var(--radius);
          cursor: pointer;
          font-size: 0.875rem;
          border: none;
          transition: all 0.2s;
        }

        .btn-primary {
          background: var(--color-primary);
          color: white;
        }

        .btn-primary:hover:not(:disabled) {
          opacity: 0.9;
        }

        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .overview-table.editable td.editable-cell {
          padding: 0.25rem;
        }

        .overview-table.editable td.editable-cell input {
          width: 100%;
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          text-align: end;
          font-size: 0.875rem;
          background: var(--color-surface);
        }

        .overview-table.editable td.editable-cell input:focus {
          outline: none;
          border-color: var(--color-primary);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        .period-header {
          position: relative;
          padding-inline-end: 1.5rem !important;
        }

        .remove-period-btn {
          position: absolute;
          top: 50%;
          inset-inline-end: 0.25rem;
          transform: translateY(-50%);
          width: 18px;
          height: 18px;
          border: none;
          background: var(--color-danger, #ef4444);
          color: white;
          border-radius: 50%;
          cursor: pointer;
          font-size: 12px;
          line-height: 1;
          opacity: 0.7;
          transition: opacity 0.2s;
        }

        .remove-period-btn:hover {
          opacity: 1;
        }

        .empty-data {
          text-align: center;
          padding: 2rem;
          color: var(--color-text-secondary);
        }

        .empty-data .btn {
          margin-top: 1rem;
        }

        .subjects-sections-grid {
          display: grid;
          gap: 1.5rem;
        }

        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1rem;
        }

        .section-header h4 {
          margin: 0;
        }

        .section-actions {
          display: flex;
          gap: 0.5rem;
        }

        .btn-ghost {
          background: none;
          border: none;
          color: var(--color-text-secondary);
          padding: 0.25rem 0.5rem;
        }

        .btn-ghost:hover {
          color: var(--color-text);
          background: var(--color-background);
        }

        .goal-input-inline {
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .goal-input-inline input {
          width: 60px;
          padding: 0.25rem 0.5rem;
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          font-size: 0.8rem;
          text-align: end;
        }

        .goal-input-inline input:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .goal-input-inline span {
          color: var(--color-text-secondary);
          font-size: 0.8rem;
        }

        .subject-group {
          margin-bottom: 1rem;
        }

        .subject-group-title {
          margin: 0 0 0.75rem;
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          padding: 0.25rem 0.5rem;
          border-radius: var(--radius);
          display: inline-block;
        }

        .subject-group-title.credit {
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
        }

        .subject-group-title.debit {
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
        }

        .subject-group-title.calculated {
          background: rgba(59, 130, 246, 0.1);
          color: #2563eb;
        }

        .subjects-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 0.75rem;
        }

        .subject-card {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius);
          padding: 0.75rem;
        }

        .subject-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .subject-name {
          font-weight: 600;
          font-size: 0.875rem;
        }

        .subject-goal {
          font-size: 0.75rem;
          color: var(--color-text-secondary);
          background: var(--color-background);
          padding: 0.125rem 0.375rem;
          border-radius: var(--radius);
        }

        .sections-list {
          border-top: 1px solid var(--color-border);
          padding-top: 0.5rem;
          margin-top: 0.25rem;
        }

        .section-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.25rem 0;
          font-size: 0.8rem;
        }

        .section-name {
          color: var(--color-text-secondary);
        }

        .section-goal {
          font-size: 0.7rem;
          color: var(--color-text-secondary);
        }

        .section-row td:first-child {
          padding-inline-start: 1.5rem;
        }

        .section-indent {
          color: var(--color-text-secondary);
          margin-inline-end: 0.5rem;
        }

        .section-cell {
          font-size: 0.8rem;
          color: var(--color-text-secondary);
        }

        .header-row {
          font-weight: 600;
          background: var(--color-surface) !important;
        }

        .header-row td {
          border-bottom: 2px solid var(--color-border);
        }

        .header-spacer {
          color: var(--color-text-secondary);
        }
      `}</style>
    </div>
  );
};
